import { MarriedOption } from '.'

import React from 'react'

import { FormikProps, FormikValues } from 'formik'

import { ProfileTypes } from '../../../../constants/applicationProfile'
import { ApplicationProfile } from '../../../../types/unlock'
import { SelectField } from '@/components/FormikInputs'
import MaskedSSNNUmberField from '@/components/FormikInputs/MaskedSSNNUmberField'
import InputField from '@/components/FormikInputs/v3/InputField'
import { Step } from '@/components/Stepper'

interface NonApplicantFormProps {
  values: FormikValues
  marriedOptions: MarriedOption[]
  applicationExternalId: string
  formikProps: FormikProps<FormikValues>
  applicationProfiles: ApplicationProfile[]
}

export const NonApplicantForm = ({
  values,
  marriedOptions,
  applicationExternalId,
  formikProps,
  applicationProfiles,
}: NonApplicantFormProps): Step => {
  const { setFieldValue, errors, touched, setFieldTouched, validateField } = formikProps

  const clearAddressFields = () => {
    setFieldValue('street', '')
    setFieldValue('city', '')
    setFieldValue('state', '')
    setFieldValue('zip_code', '')
    setFieldValue('unit_number', '')
  }

  const handleMarriedToChange = (value: React.ChangeEvent<HTMLSelectElement>) => {
    if (values.contact_type === ProfileTypes.NOO) {
      return
    }

    if (!value.target.value) {
      clearAddressFields()
      return
    }

    const profile = applicationProfiles.find(
      (profile) => profile.id.toString() === value.target.value
    )

    if (
      !profile ||
      !profile.profile.mailing_address ||
      (profile.profile_type !== ProfileTypes.APPLICANT &&
        profile.profile_type !== ProfileTypes.CO_OWNER &&
        profile.profile_type !== ProfileTypes.NOO)
    ) {
      clearAddressFields()
      return
    }

    setFieldValue('street', profile.profile.mailing_address.street)
    setFieldValue('city', profile.profile.mailing_address.city)
    setFieldValue('state', profile.profile.mailing_address.state)
    setFieldValue('zip_code', profile.profile.mailing_address.zip_code)
    setFieldValue('unit_number', profile.profile.mailing_address.unit_number)
  }

  return {
    title: 'Non-Applicant Information',
    content: () => (
      <div className='flex flex-col w-full gap-3'>
        <InputField
          name='first_name'
          placeholder='First Name*'
          style='default'
        />
        <InputField
          name='middle_name'
          placeholder='Middle Name'
          style='default'
        />
        <InputField
          name='last_name'
          placeholder='Last Name*'
          style='default'
        />
        <SelectField
          extraClasses='w-full'
          placeholder='Married To*'
          name='married_to'
          options={marriedOptions}
          onChange={handleMarriedToChange}
          style='default'
        />
        <InputField
          name='born_on'
          type='date'
          placeholder='DOB'
          parentClassName='w-full'
          style='default'
        />
        <MaskedSSNNUmberField
          placeholder='SSN'
          name='social_security_number'
          format='###-##-####'
          maskedFormat='***-**-####'
          type='text'
          isNumericString={true}
          setFieldValue={setFieldValue}
          parentClassName='w-full'
          customRight='right-1'
          app={applicationExternalId}
          style='default'
          showError
          errors={errors}
          touched={touched}
          afterOnChange={() => {
            setFieldTouched('social_security_number', true)
            setTimeout(() => {
              validateField('social_security_number')
            }, 0)
          }}
        />
        <InputField
          name='phone'
          placeholder='Phone'
          parentClassName='w-full'
          style='default'
        />
        <InputField
          name='email'
          placeholder='E-mail*'
          parentClassName='w-full'
          style='default'
          onBlur={() => {
            setFieldTouched('email')
            validateField('email')
          }}
        />
        <InputField
          parentClassName='w-full'
          placeholder='Street*'
          name='street'
          style='default'
          data-test='street-input'
        />
        <div className='flex flex-row gap-1 text-xs'>
          <InputField
            parentClassName='w-full'
            placeholder='Unit Number'
            name='unit_number'
            style='default'
            data-test='unit-number-input'
          />
          <InputField
            parentClassName='w-full'
            placeholder='City*'
            name='city'
            style='default'
            data-test='city-input'
          />
        </div>
        <div className='flex flex-row gap-1 text-xs'>
          <InputField
            parentClassName='w-full'
            placeholder='State*'
            name='state'
            style='default'
            data-test='state-input'
          />
          <InputField
            parentClassName='w-full'
            placeholder='Zip Code*'
            name='zip_code'
            style='default'
            data-test='zipcode-input'
          />
        </div>
        <SelectField
          extraClasses='w-full'
          placeholder='US Citizen / Has Green Card'
          name='is_us_citizen_or_green_card_holder'
          options={[
            { label: 'Yes', value: true },
            { label: 'No', value: false },
          ]}
          style='default'
        />
        <InputField
          name='photo_id_expire'
          placeholder='Photo ID Expire'
          parentClassName='w-full'
          style={'default'}
          type='date'
        />
      </div>
    ),
    buttonLabel: 'Next',
    disabledStep: !values.contact_type || !values.currentMode,
    hideButton: true,
  }
}
